import React, { PropsWithChildren, useCallback, useId, useRef, useState } from "react";
import {
  Modal as ChakraModal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
} from "@chakra-ui/react";
import { useSearchParams } from "react-router";

type ModalSize = "xs" | "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "4xl" | "5xl" | "6xl" | "full";
type ModalScrollBehavior = "inside" | "outside";

interface UseModalProps {
  id?: string;
  headerTitle: string;
  headerFontSize?: string;
  closeOnOverlayClick?: boolean;
  isCentered?: boolean;
  size: ModalSize;
  scrollBehavior?: ModalScrollBehavior;
  queryParam?: string;
}

interface UseModalReturn {
  open: () => void;
  close: () => void;
  Modal: React.FC<PropsWithChildren>;
}

export default function useModal({
  id: providedId,
  headerTitle,
  headerFontSize,
  size,
  scrollBehavior = "inside",
  closeOnOverlayClick = true,
  isCentered = false,
  queryParam = undefined,
}: UseModalProps): UseModalReturn {
  const generatedId = useId();
  const id = providedId || generatedId;
  const [searchParams, setSearchParams] = useSearchParams();
  const [modalOpen, setModalOpen] = useState(
    queryParam ? ["true", "t", "1", "y", "yes", "on"].includes(searchParams.get(queryParam) || "") : false,
  );

  const openModal = useCallback(() => setModalOpen(true), []);
  const closeModal = useCallback(() => {
    if (queryParam) {
      searchParams.delete(queryParam);
      setSearchParams(searchParams);
    }
    setModalOpen(false);
  }, [searchParams, setSearchParams, queryParam]);

  const { isOpen, onOpen, onClose } = useDisclosure({
    id,
    isOpen: modalOpen,
    onOpen: openModal,
    onClose: closeModal,
  });

  const initialRef = useRef(null);
  const finalRef = useRef(null);

  const ModalComponent: React.FC<PropsWithChildren> = useCallback(
    ({ children }) => (
      <ChakraModal
        isOpen={isOpen}
        onClose={onClose}
        size={size}
        scrollBehavior={scrollBehavior}
        closeOnOverlayClick={closeOnOverlayClick}
        isCentered={isCentered}
        initialFocusRef={initialRef}
        finalFocusRef={finalRef}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize={headerFontSize || "24"} paddingBottom={0}>
            {headerTitle}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>{children}</ModalBody>
        </ModalContent>
      </ChakraModal>
    ),
    [isOpen, onClose, size, scrollBehavior, closeOnOverlayClick, isCentered, headerTitle, headerFontSize],
  );

  return { open: onOpen, close: onClose, Modal: ModalComponent };
}
